/* Feb 2026 · Reminders Overhaul — searchable/sortable picker used
 * everywhere a reminder needs to bind to a person or workspace entity
 * (delegate / client / brand / project / task). Alphabetised, keyboard-
 * accessible, and preserves the current selection while the user types.
 */
import { useMemo, useState } from "react";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from "@/components/ui/command";
import { Check, ChevronDown, X } from "lucide-react";

/**
 * Options prop shape: [{ id, label, sublabel?, }]
 * `value` = selected id (or "").
 * `onChange(id | "")` — pass empty string to clear.
 */
export default function ReminderEntityPicker({
  value = "",
  options = [],
  onChange,
  placeholder = "Select…",
  emptyMsg = "No matches.",
  size = "sm",              // sm | xs
  className = "",
  disabled = false,
  clearable = true,
  testid,
}) {
  const [open, setOpen] = useState(false);
  const sorted = useMemo(() => {
    const arr = (options || []).map((o) => ({ ...o })).filter((o) => o && o.id);
    arr.sort((a, b) => (a.label || "").toLowerCase().localeCompare((b.label || "").toLowerCase()));
    return arr;
  }, [options]);
  const selected = sorted.find((o) => o.id === value) || (value ? { id: value, label: value } : null);
  const h = size === "xs" ? "h-7" : "h-8";
  const txt = size === "xs" ? "text-[11px]" : "text-xs";

  return (
    <Popover open={open} onOpenChange={disabled ? undefined : setOpen}>
      <PopoverTrigger asChild>
        <button
          type="button"
          disabled={disabled}
          className={`w-full ${h} ${txt} px-2 bg-background border border-border hover:border-primary/60 flex items-center gap-1.5 disabled:opacity-60 text-left ${className}`}
          data-testid={testid}
        >
          <span className={`flex-1 truncate ${selected ? "text-foreground" : "text-muted-foreground"}`}>
            {selected ? selected.label : placeholder}
          </span>
          {clearable && selected && !disabled && (
            <span
              role="button"
              tabIndex={0}
              onClick={(e) => { e.stopPropagation(); onChange && onChange(""); }}
              onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") { e.stopPropagation(); onChange && onChange(""); } }}
              className="p-0.5 text-muted-foreground hover:text-foreground shrink-0 cursor-pointer"
              data-testid={testid ? `${testid}-clear` : undefined}
              aria-label="Clear"
            >
              <X className="w-3 h-3" />
            </span>
          )}
          <ChevronDown className="w-3 h-3 text-muted-foreground shrink-0" />
        </button>
      </PopoverTrigger>
      <PopoverContent
        className="w-72 p-0 rounded-none"
        onPointerDownOutside={() => setOpen(false)}
        align="start"
        data-testid={testid ? `${testid}-popover` : undefined}
      >
        <Command>
          <CommandInput
            placeholder="Type to search…"
            className="h-9 text-xs"
            data-testid={testid ? `${testid}-search` : undefined}
          />
          <CommandList className="max-h-72">
            <CommandEmpty>{emptyMsg}</CommandEmpty>
            <CommandGroup>
              {sorted.map((o) => {
                const isSel = o.id === value;
                return (
                  <CommandItem
                    key={o.id}
                    value={`${o.label} ${o.sublabel || ""}`}
                    onSelect={() => { onChange && onChange(o.id); setOpen(false); }}
                    data-testid={testid ? `${testid}-opt-${o.id}` : undefined}
                  >
                    <div className="flex-1 min-w-0">
                      <div className="truncate">{o.label}</div>
                      {o.sublabel && (
                        <div className="text-[10px] text-muted-foreground truncate">{o.sublabel}</div>
                      )}
                    </div>
                    {isSel && <Check className="ml-auto w-3.5 h-3.5 shrink-0" />}
                  </CommandItem>
                );
              })}
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}
